import React, {useState} from 'react';
import {useTracking} from 'react-tracking';

import {IconButton, Menu} from '@albert-io/atomic';

import {
  COPYRIGHT_TEXT,
  CopyrightModalContent
} from 'client/components/CopyrightModal/CopyrightModal.react';
import {IssuesModal} from 'client/Modals/IssuesModal/IssuesModal';
import QuestionTrackerModal from 'client/components/PracticeView/QuestionTrackerModal/QuestionTrackerModal.react';

interface Props {
  questionId: string;
  /** Whether to include the "Question feedback" item (requires a valid session). */
  showFeedback?: boolean;
  /**
   * When both are provided, includes a "My usage" item that opens the question
   * tracker modal. Supplied by the fused FRQ grading footer.
   */
  questionSetId?: string;
  userId?: string;
}

/**
 * Single overflow menu for the fused FRQ grading footer, opened from an
 * `info-circle` ("ⓘ") trigger.
 *
 * Consolidates every footer affordance into one menu — question feedback, the
 * teacher's "My usage" view, and the copyright affordance (which stands in for
 * the hidden page-level `<Footer minimal />`) — so the toolbar needs only a
 * single icon instead of separate "info" and "more" buttons.
 */
export default function QuestionActionsMenu({
  questionId,
  showFeedback = true,
  questionSetId,
  userId
}: Props) {
  const {trackEvent} = useTracking();
  const [isCopyrightModalOpen, setIsCopyrightModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

  const showUsage = Boolean(questionSetId && userId);

  return (
    <>
      <Menu position='top-start' positionFixed closeOnItemClick>
        <Menu.Trigger>
          {(triggerProps) => (
            <IconButton
              icon={['far', 'info-circle']}
              label='More info'
              tooltip='More info'
              tooltipProps={{placement: 'top'}}
              data-testid='practice-view__question-actions-menu-btn'
              {...triggerProps}
            />
          )}
        </Menu.Trigger>
        <Menu.Content>
          {showFeedback && (
            <Menu.Item
              leftIconProps={{icon: ['far', 'message-question']}}
              onClick={() => setIsFeedbackModalOpen(true)}
            >
              Question feedback
            </Menu.Item>
          )}
          {showUsage && (
            <Menu.Item
              leftIconProps={{icon: ['far', 'chart-line']}}
              onClick={() => {
                trackEvent({content: 'My usage', event: 'click', type: 'button'});
                setIsUsageModalOpen(true);
              }}
            >
              My usage
            </Menu.Item>
          )}
          {/* Matches the copyright item in the adaptive practice view dropdown. */}
          <Menu.Item
            textProps={{size: 'xs', color: 'tertiary', className: 'text-nowrap text-center'}}
            onClick={() => setIsCopyrightModalOpen(true)}
            className='items-center'
          >
            {COPYRIGHT_TEXT}
          </Menu.Item>
        </Menu.Content>
      </Menu>
      {isCopyrightModalOpen && (
        <CopyrightModalContent onClose={() => setIsCopyrightModalOpen(false)} />
      )}
      {isFeedbackModalOpen && (
        <IssuesModal questionId={questionId} handleClose={() => setIsFeedbackModalOpen(false)} />
      )}
      {isUsageModalOpen && questionSetId && userId && (
        <QuestionTrackerModal
          questionSetId={questionSetId}
          userId={userId}
          handleClose={() => setIsUsageModalOpen(false)}
        />
      )}
    </>
  );
}
